import { api } from '.';
import {
  IChatCharacter,
  IChatPagination,
  IChatResponse,
  IComment,
  ICommunityPost,
  IEFinderObject,
  IPagination,
  IStory,
} from '../model';
import { toQueryString } from '../utils/misc';
import { CHAT_BOT_API } from './chatbot';

export type IFollowingFeed =
  | {
      id: string;
      objectType: 'story';
      objectId: number;
      object?: IStory;
      published: string;
    }
  | {
      id: string;
      objectType: 'community';
      objectId: number;
      object?: ICommunityPost;
      published: string;
    };

const _populate = (
  feeds: IFollowingFeed[],
  type: 'story' | 'community',
  objects: (IStory | ICommunityPost)[]
) => {
  return feeds.map((item) => {
    const object = objects.find(
      (s) => s.id === item.objectId && type === item.objectType
    );
    if (object) {
      return {
        ...item,
        id: `${item.objectType}-${item.objectId}`,
        object,
      } as IFollowingFeed;
    }

    return {
      ...item,
      id: `${item.objectType}-${item.objectId}`,
    };
  });
};

export const fetchFollowingFeeds = async (page = 1, perPage = 20) => {
  const { data } = await api.get<IPagination<IFollowingFeed>>(
    `/users/me/following-feeds/${toQueryString({ page, perPage })}`
  );

  const storyIds = data.items
    .filter((item) => item.objectType === 'story')
    .map((item) => item.objectId);
  const postIds = data.items
    .filter((item) => item.objectType === 'community')
    .map((item) => item.objectId);

  let feeds = data.items;
  if (storyIds.length > 0) {
    const { data: stories } = await api.get<IStory[]>(
      `/stories/${toQueryString({ storyIds: storyIds.join(',') })}`
    );
    feeds = _populate(feeds, 'story', stories);
  }

  if (postIds.length > 0) {
    const { data: posts } = await api.get<ICommunityPost[]>(
      `/communities/${toQueryString({ communityIds: postIds.join(',') })}`
    );
    feeds = _populate(feeds, 'community', posts);
  }

  return {
    ...data,
    items: feeds,
  };
};

const _populateHome = (feeds: IGroundFeed[], objects: IGroundPost[]) => {
  return feeds.map((item) => {
    const object = objects.find((s) => s.id === item.media_id);
    if (object) {
      return {
        ...item,
        post: object,
      } as IGroundFeed;
    }
    return item;
  });
};

export type FeedCodeType =
  | 'by_tag'
  | 'popular'
  | 'played_character'
  | 'my_character'
  | 'recommend_character';

export interface IGroundFeed {
  id: number;
  media_id: number;
  media_type: 'playground';
  media_payload: object;
  extra: object;
  character: IChatCharacter;
  created_at: string;
  updated_at: string;
  post?: IGroundPost;
}

export interface IGroundPost {
  id: number;
  objectType: 'user' | 'character';
  objectDd: number;
  content: string;
  picture: IEFinderObject<'image'>;
  pictures: IEFinderObject<'image'>[];
  isFixed: boolean;
  isUpdated: string;
  isCommentEnabled: string;
  isLiked: boolean;
  viewCount: number;
  likeCount: number;
  commentCount: number;
  user: {
    id: number;
    nickname: string;
    picture: IEFinderObject<'image'>;
  };
  previewComments: IComment[];
  modified: string;
  created: string;
}

export const fetchHomeFeeds = async (
  feed_code: FeedCodeType,
  query: undefined | string,
  page = 1,
  perPage = 20
) => {
  const { data } = await CHAT_BOT_API.get<
    IChatResponse<IChatPagination<IGroundFeed>>
  >(
    query
      ? `/character-feeds/${feed_code}/${query}&page=${page}&per_page=${perPage}`
      : `/character-feeds/${feed_code}/${toQueryString({
          page,
          perPage,
        })}`
  );
  const result = data.result;
  const groundIds = result.items
    .filter((item) => item.media_type === 'playground')
    .map((item) => item.media_id);

  let feeds = result.items;
  if (groundIds.length > 0) {
    const { data } = await api.get<IPagination<IGroundPost>>(
      `/play/characters/all/communities/${toQueryString({
        community_ids: groundIds.join(','),
      })}`
    );
    feeds = _populateHome(feeds, data.items);
  }

  return { ...result, items: feeds };
};
